import { useMemo, useState } from 'react';
import { useEmergencyStore } from '@/stores/emergency-store';
import type { SiteMarker, SiteSeverity, ArcEventLite } from '@/stores/emergency-store';
import { Building2, MapPin, AlertTriangle, ShieldCheck, Search, Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// Colour map mirrors the marker palette in EmergencyMap.tsx so the list and
// the map agree on what each tier looks like. The brand navy is the resting
// state (no events / dispatched), red marks open activity, and a vivid amber
// flags priority sites with many simultaneous events.
const TIER_TEXT: Record<SiteSeverity, string> = {
  idle: 'text-blue-900 dark:text-blue-300',
  info: 'text-red-600 dark:text-red-400',
  warning: 'text-red-600 dark:text-red-400',
  alarm: 'text-red-700 dark:text-red-400',
  critical: 'text-amber-600 dark:text-amber-400',
  dispatched: 'text-blue-900 dark:text-blue-300',
};
const TIER_BG: Record<SiteSeverity, string> = {
  idle: 'bg-blue-100 dark:bg-blue-900/40',
  info: 'bg-red-100 dark:bg-red-900/40',
  warning: 'bg-red-100 dark:bg-red-900/40',
  alarm: 'bg-red-100 dark:bg-red-900/50',
  critical: 'bg-amber-100 dark:bg-amber-900/50',
  dispatched: 'bg-blue-100 dark:bg-blue-900/40',
};
const TIER_LABEL: Record<SiteSeverity, string> = {
  idle: 'Sin eventos',
  info: 'Evento informativo',
  warning: 'Evento activo',
  alarm: 'Alarma',
  critical: 'Prioritario',
  dispatched: 'Patrulla en camino',
};

export function SiteList() {
  const { sites, arcEvents, selectSite } = useEmergencyStore();
  const [filter, setFilter] = useState('');
  // Default to showing every account so operators see the full roster on
  // first paint. They can opt-in to the "active events only" view via the
  // toggle below when triaging a busy night.
  const [onlyActive, setOnlyActive] = useState(false);

  // Group open events by site so we can render the latest event preview
  // inline without an extra lookup per row.
  const eventsBySite = useMemo(() => {
    const map = new Map<string, ArcEventLite[]>();
    for (const ev of arcEvents) {
      if (!ev.site_id) continue;
      const arr = map.get(ev.site_id) ?? [];
      arr.push(ev);
      map.set(ev.site_id, arr);
    }
    return map;
  }, [arcEvents]);

  const filtered = useMemo(() => {
    // DataTables-style search: split the query into whitespace-separated
    // tokens and require every token to match any of the searchable fields
    // (account name/cliente, site name, account number, address). Both the
    // haystack and the tokens are normalised (lower-cased + diacritics
    // stripped) so "jose" matches "José" and "z2" matches "Zona 2".
    const normalise = (value: string) =>
      value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const tokens = normalise(filter).split(/\s+/).filter(Boolean);
    return sites
      .filter((s) => (onlyActive ? s.open_events_count > 0 : true))
      .filter((s) => {
        if (tokens.length === 0) return true;
        const haystack = normalise(
          [s.account_name, s.name, s.account_number, s.address]
            .filter(Boolean)
            .join(' '),
        );
        return tokens.every((t) => haystack.includes(t));
      })
      // Sites with the highest urgency first; ties broken by event count
      .sort((a, b) => {
        const tierRank: Record<SiteSeverity, number> = {
          critical: 5, alarm: 4, dispatched: 3, warning: 2, info: 1, idle: 0,
        };
        const t = tierRank[b.max_severity] - tierRank[a.max_severity];
        if (t !== 0) return t;
        return b.open_events_count - a.open_events_count;
      });
  }, [sites, filter, onlyActive]);

  const handleSiteClick = (site: SiteMarker) => {
    // Open the rich SiteDetail card. The operator can then drill into a
    // specific open event from inside that card if they wish; we no longer
    // auto-open the most-recent event because the detail card already lists
    // them with one-click navigation.
    selectSite(site);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Filter bar */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-800 space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Buscar por # cuenta, cliente, sitio o dirección..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-slate-800 border border-transparent focus:border-amber-500 outline-none text-gray-900 dark:text-white"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
            className="rounded"
          />
          Mostrar solo sitios con eventos activos
        </label>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 dark:text-slate-400">
            <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            {onlyActive
              ? 'Sin eventos activos en sitios.'
              : sites.length === 0
                ? 'No hay cuentas registradas.'
                : 'Ningún sitio coincide con el filtro.'}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-slate-800">
            {filtered.map((site) => {
              const events = eventsBySite.get(site.id) ?? [];
              const latest = events[0];
              return (
                <li key={site.id}>
                  <button
                    onClick={() => handleSiteClick(site)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${TIER_BG[site.max_severity]} flex-shrink-0`}>
                        <Building2 className={`w-5 h-5 ${TIER_TEXT[site.max_severity]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          {/* Account number badge — surfaced on its own pill so
                              it is always visible even when the title is long
                              enough to truncate. Falls back to a placeholder
                              when the site has not been linked to an account. */}
                          <span
                            className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${
                              site.account_number
                                ? 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-300'
                                : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500'
                            }`}
                          >
                            {site.account_number ? `#${site.account_number}` : 'sin cuenta'}
                          </span>
                          {site.open_events_count > 0 && (
                            <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${TIER_BG[site.max_severity]} ${TIER_TEXT[site.max_severity]}`}>
                              <Bell className="w-3 h-3" />
                              {site.open_events_count}
                            </span>
                          )}
                        </div>
                        <p className="font-semibold text-sm text-gray-900 dark:text-white truncate mt-0.5">
                          {site.account_name ? `${site.account_name} \u00b7 ${site.name}` : site.name}
                        </p>
                        {site.address && (
                          <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{site.address}</span>
                          </p>
                        )}
                        <p className={`text-[11px] mt-1 font-medium ${TIER_TEXT[site.max_severity]}`}>
                          {TIER_LABEL[site.max_severity]}
                        </p>
                        {latest && (
                          <div className="mt-2 px-2 py-1.5 rounded-md bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/50">
                            <div className="flex items-center gap-1.5 text-[11px] text-gray-700 dark:text-slate-300">
                              <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                              <span className="font-medium truncate">
                                {latest.description_es ?? `CID ${latest.cid_code}`}
                              </span>
                            </div>
                            <p className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">
                              {formatDistanceToNow(new Date(latest.received_at), { addSuffix: true, locale: es })}
                              {latest.zone_no != null && ` · Zona ${latest.zone_no}`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
